import api from './api'

// TẤT CẢ requests đều qua backend proxy để:
// 1. Tránh Mixed Content (HTTPS frontend -> HTTP news API)
// 2. Backend thêm User-Agent header (browser không cho phép)
// 3. Backend thêm X-API-Token header

// Helper to check if user is authenticated
const isAuthenticated = () => {
  return !!localStorage.getItem('access_token')
}

// Get endpoint path - use /public prefix for guests
const getEndpoint = (path) => {
  return isAuthenticated() ? `/news${path}` : `/news/public${path}`
}

const newsService = {
  /**
   * Lấy danh sách tin tức với nhiều filter options
   * @param {Object} params - Filter params
   * @param {number} params.page - Trang (default 1)
   * @param {number} params.limit - Số tin mỗi trang (default 20, max 50)
   * @param {string} params.location - Địa phương (da-nang, hue, ...)
   * @param {string} params.source - Nguồn tin
   * @param {string} params.category - Danh mục (thi_truong, quy_hoach, ...)
   * @param {string} params.customer_type - Đối tượng khách (mua_o, dau_tu, all)
   * @param {string} params.sub_category - Danh mục phụ (can_ho, dat_nen, ...)
   * @param {string} params.sentiment - Tâm lý (positive, negative, neutral)
   * @param {string} params.price_segment - Phân khúc giá (binh_dan, trung_cap, cao_cap, hang_sang)
   * @param {string} params.property_type - Loại BĐS
   * @param {boolean} params.is_hot - Chỉ lấy tin nóng
   * @param {number} params.min_score - Điểm tối thiểu
   * @param {string} params.time_range - Thời gian (today, 24h, 3d, 7d, 30d)
   * @param {string} params.sort - Sắp xếp (latest, score, published, hot)
   */
  async getNews(params = {}) {
    try {
      // Use public endpoint for guests, authenticated endpoint for logged-in users
      const endpoint = isAuthenticated() ? '/news' : '/news/public'
      const response = await api.get(endpoint, { params })
      return response.data
    } catch {
      // Fallback: trả về empty data khi API không khả dụng
      return {
        status: 'success',
        data: [],
        pagination: {
          total: 0,
          page: 1,
          limit: 20,
          total_pages: 0,
          has_more: false,
        },
      }
    }
  },

  /**
   * Lấy tin nóng (score >= 70)
   */
  async getHotNews(params = {}) {
    try {
      const endpoint = isAuthenticated() ? '/news/hot' : '/news/public/hot'
      const response = await api.get(endpoint, { params })
      return response.data
    } catch {
      // Fallback: trả về empty data
      return { status: 'success', data: [] }
    }
  },

  /**
   * Lấy tin mới nhất
   */
  async getLatestNews(limit = 10) {
    try {
      const response = await api.get('/news/latest', { params: { limit } })
      return response.data
    } catch {
      // Fallback: trả về empty data
      return { status: 'success', data: [] }
    }
  },

  /**
   * Lấy chi tiết tin tức (bao gồm content, related_news, usage_suggestions)
   */
  async getNewsDetail(id) {
    try {
      const endpoint = isAuthenticated() ? `/news/${id}` : `/news/public/${id}`
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return { status: 'error', message: 'Không thể tải bài viết' }
    }
  },

  /**
   * Tìm kiếm tin tức
   */
  async searchNews(query, params = {}) {
    try {
      const endpoint = isAuthenticated() ? '/news/search' : '/news/public/search'
      const response = await api.get(endpoint, {
        params: { q: query, ...params },
      })
      return response.data
    } catch {
      // Fallback: trả về empty data
      return { status: 'success', data: [], pagination: { total: 0, page: 1, has_more: false } }
    }
  },

  /**
   * Autocomplete gợi ý khi nhập từ khóa tìm kiếm
   * @param {string} query - Từ khóa đang nhập (min 2 chars)
   * @param {number} limit - Số gợi ý tối đa (default 10)
   */
  async getSearchAutocomplete(query, limit = 10) {
    try {
      const endpoint = isAuthenticated() ? '/news/search/autocomplete' : '/news/public/search/autocomplete'
      const response = await api.get(endpoint, {
        params: { q: query, limit },
      })
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Lấy gợi ý tìm kiếm (trending keywords, popular locations/categories)
   * @param {Object} params - Filter params
   * @param {string} params.location - Filter theo địa phương
   * @param {string} params.category - Filter theo danh mục
   * @param {number} params.limit - Số gợi ý tối đa
   */
  async getSearchSuggestions(params = {}) {
    try {
      const response = await api.get('/news/search/suggestions', { params })
      return response.data
    } catch {
      return {
        status: 'success',
        data: {
          trending_keywords: [],
          popular_locations: [],
          popular_categories: [],
          hot_suggestions: [],
        },
      }
    }
  },

  /**
   * Lấy data trang chủ (hot + latest + by location)
   */
  async getHomepageData() {
    try {
      const response = await api.get('/news/homepage')
      return response.data
    } catch {
      // Fallback: trả về empty data
      return {
        status: 'success',
        data: {
          hot_news: [],
          latest_news: [],
          by_location: {},
          stats: { total_news: 0, today_news: 0 },
        },
      }
    }
  },

  /**
   * Lấy danh sách danh mục tin
   * @param {boolean} includeSubs - Bao gồm sub-categories
   * @param {boolean} includeStats - Bao gồm thống kê số tin
   */
  async getCategories(includeSubs = true, includeStats = true) {
    try {
      const endpoint = isAuthenticated() ? '/news/categories' : '/news/public/categories'
      const response = await api.get(endpoint, {
        params: {
          include_subs: includeSubs,
          stats: includeStats,
        },
      })
      return response.data
    } catch {
      // Fallback data
      return {
        status: 'success',
        data: [
          { slug: 'thi_truong', name: 'Thị trường', icon: 'chart-line', color: '#3B82F6' },
          { slug: 'quy_hoach', name: 'Quy hoạch', icon: 'map', color: '#10B981' },
          { slug: 'chinh_sach', name: 'Chính sách', icon: 'document-text', color: '#8B5CF6' },
          { slug: 'tai_chinh_bds', name: 'Tài chính BĐS', icon: 'currency-dollar', color: '#F59E0B' },
          { slug: 'xay_dung', name: 'Xây dựng', icon: 'office-building', color: '#EF4444' },
          { slug: 'kinh_te', name: 'Kinh tế vĩ mô', icon: 'trending-up', color: '#6366F1' },
        ],
      }
    }
  },

  /**
   * Lấy danh sách loại khách hàng
   */
  async getCustomerTypes(includeStats = true) {
    try {
      const response = await api.get('/news/customer-types', {
        params: { stats: includeStats },
      })
      return response.data
    } catch {
      // Fallback data
      return {
        status: 'success',
        data: [
          { slug: 'mua_o', name: 'Khách mua để ở', icon: 'home', color: '#10B981' },
          { slug: 'dau_tu', name: 'Khách đầu tư', icon: 'trending-up', color: '#3B82F6' },
          { slug: 'all', name: 'Tất cả khách hàng', icon: 'users', color: '#6B7280' },
        ],
      }
    }
  },

  /**
   * Lấy danh sách địa phương
   */
  async getLocations(includeStats = false) {
    try {
      const endpoint = isAuthenticated() ? '/news/locations' : '/news/public/locations'
      const response = await api.get(endpoint, {
        params: { stats: includeStats },
      })
      return response.data
    } catch {
      // Fallback data
      return {
        status: 'success',
        data: [
          { slug: 'da-nang', name: 'Đà Nẵng' },
          { slug: 'hue', name: 'Huế' },
          { slug: 'quang-nam', name: 'Quảng Nam' },
          { slug: 'khanh-hoa', name: 'Khánh Hòa' },
        ],
      }
    }
  },

  /**
   * Lấy danh sách nguồn tin
   */
  async getSources(includeStats = false) {
    try {
      const endpoint = isAuthenticated() ? '/news/sources' : '/news/public/sources'
      const response = await api.get(endpoint, {
        params: { stats: includeStats },
      })
      // Normalize: backend dùng "key", frontend cần "slug"
      if (response.data?.data) {
        response.data.data = response.data.data.map(source => ({
          ...source,
          slug: source.slug || source.key,
        }))
      }
      return response.data
    } catch {
      // Fallback data
      return {
        status: 'success',
        data: [
          { slug: 'vnexpress', name: 'VnExpress' },
          { slug: 'baomoi', name: 'Báo Mới' },
          { slug: 'cafef', name: 'CafeF' },
        ],
      }
    }
  },

  /**
   * Lấy thống kê công khai
   */
  async getStats() {
    try {
      const response = await api.get('/news/stats')
      return response.data
    } catch {
      // Fallback data
      return {
        status: 'success',
        data: {
          total_news: 0,
          today_news: 0,
          hot_news: 0,
        },
      }
    }
  },

  // ============================================================
  // User-specific endpoints
  // ============================================================

  async saveNews(newsId) {
    const response = await api.post(`/news/${newsId}/save`)
    return response.data
  },

  async unsaveNews(newsId) {
    const response = await api.delete(`/news/${newsId}/unsave`)
    return response.data
  },

  async shareNews(newsId, data = {}) {
    const response = await api.post(`/news/${newsId}/share`, data)
    return response.data
  },

  async getSavedNews() {
    const response = await api.get('/news/my-saved')
    return response.data
  },

  async getSharedNews() {
    const response = await api.get('/news/my-shared')
    return response.data
  },

  // ============================================================
  // Helper functions
  // ============================================================

  /**
   * Các option cho sentiment filter
   */
  getSentimentOptions() {
    return [
      { value: '', label: 'Tất cả tin' },
      { value: 'positive', label: 'Tích cực', color: '#10B981', description: 'Tin tốt cho thị trường' },
      { value: 'neutral', label: 'Trung tính', color: '#6B7280', description: 'Tin thông thường' },
      { value: 'negative', label: 'Tiêu cực', color: '#EF4444', description: 'Cần chú ý' },
    ]
  },

  /**
   * Các option cho price segment filter
   */
  getPriceSegmentOptions() {
    return [
      { value: '', label: 'Tất cả phân khúc' },
      { value: 'binh_dan', label: 'Bình dân (< 2 tỷ)', color: '#10B981' },
      { value: 'trung_cap', label: 'Trung cấp (2-5 tỷ)', color: '#3B82F6' },
      { value: 'cao_cap', label: 'Cao cấp (5-10 tỷ)', color: '#8B5CF6' },
      { value: 'hang_sang', label: 'Hạng sang (> 10 tỷ)', color: '#F59E0B' },
    ]
  },

  /**
   * Các option cho sort
   */
  getSortOptions() {
    return [
      { value: 'latest', label: 'Mới nhất' },
      { value: 'hot', label: 'Tin nổi bật' },
      { value: 'score', label: 'Điểm chất lượng' },
    ]
  },

  /**
   * Các option cho time range
   */
  getTimeRangeOptions() {
    return [
      { value: '', label: 'Toàn bộ' },
      { value: 'today', label: 'Hôm nay' },
      { value: '24h', label: '24 giờ qua' },
      { value: '3d', label: '3 ngày gần đây' },
      { value: '7d', label: 'Tuần này' },
      { value: '30d', label: 'Tháng này' },
    ]
  },
}

export default newsService
